/* eslint @typescript-eslint/no-explicit-any: 0 */
/* eslint @typescript-eslint/explicit-module-boundary-types: 0 */

export interface LoggerOptions {
  customerToken: string;
  tags?: string[];
  enableConsoleLogs?: boolean;
  enableFlushLogs?: boolean;
}

export type LogLevel = 'debug' | 'info' | 'warn' | 'error';

interface LogEvent {
  level: LogLevel;
  message: string;
  timestamp: Date;
  [key: string]: any;
}

const initialFlushDelaySeconds = 10;
const maxRetries = 4;

export default class LogglyLogger {
  private logEvents: LogEvent[] = [];
  private timeout: ReturnType<typeof setTimeout> | null = null;
  private flushDelaySeconds: number = initialFlushDelaySeconds;
  private globalContext: { [key: string]: any } = {};
  private retries = 0;

  constructor(private options: LoggerOptions) {
    const { enableFlushLogs } = this.options;

    if (enableFlushLogs) {
      this.startFlushTimeout(this.flushDelaySeconds);
    }
  }

  public debug(message: any, context?: { [key: string]: any }): void {
    this.logWithLevel('debug', message, context);
  }

  public info(message: any, context?: { [key: string]: any }): void {
    this.logWithLevel('info', message, context);
  }

  public warn(message: any, context?: { [key: string]: any }): void {
    this.logWithLevel('warn', message, context);
  }

  public error(message: any, context?: { [key: string]: any }): void {
    if (message instanceof Error) {
      this.logWithLevel('error', message.message, context);
    } else {
      this.logWithLevel('error', message, context);
    }
  }

  public setGlobalContext(globalContext: { [key: string]: any }): void {
    this.globalContext = globalContext;
  }

  public setEnableConsoleLogs(enable: boolean): void {
    this.options.enableConsoleLogs = enable;
  }

  public mergeGlobalContext(globalContext: { [key: string]: any }): void {
    this.globalContext = {
      ...this.globalContext,
      ...globalContext,
    };
  }

  public destroy(): void {
    if (this.timeout) {
      clearTimeout(this.timeout);
    }

    this.logEvents = [];
  }

  private logWithLevel(
    level: LogLevel,
    message: any,
    localContext?: { [key: string]: any },
  ): void {
    const { enableConsoleLogs, enableFlushLogs } = this.options;
    const context = { ...this.globalContext, ...localContext };

    if (enableFlushLogs) {
      this.logEvents.push({
        ...context,
        level,
        message: String(message),
        timestamp: new Date(),
      });
    }

    if (enableConsoleLogs) {
      if (typeof console.groupCollapsed === 'function') {
        console.groupCollapsed(message);
        console[level](message, context);
        console.groupEnd();
      } else {
        console[level](message);
      }
    }
  }

  private startFlushTimeout(flushDelaySeconds: number): void {
    if (this.timeout) {
      clearTimeout(this.timeout);
    }

    this.flushDelaySeconds = flushDelaySeconds;

    this.timeout = setTimeout(() => {
      this.flush();
    }, this.flushDelaySeconds * 1000);
  }

  private async flush(): Promise<void> {
    const { customerToken, tags = [] } = this.options;
    try {
      if (this.logEvents.length > 0) {
        // TODO: Merge log event context with global context
        // TODO: Send log events to Loggly

        const url = `https://logs-01.loggly.com/bulk/${customerToken}/`;
        const res = await fetch(url, {
          method: 'post',
          mode: 'cors',
          headers: {
            'Content-Type': 'application/json',
            'X-LOGGLY-TAG': tags.join(','),
          },
          body: this.logEvents
            .map((logEvent) => JSON.stringify(logEvent))
            .join('\n'),
        });

        if (!res.ok) {
          throw new Error(`Invalid response '${res.status}'`);
        }

        this.logEvents = [];
      }

      this.startFlushTimeout(initialFlushDelaySeconds);
    } catch (err) {
      if (this.retries > maxRetries) {
        console.warn(
          `Failed to send logs to Loggly with '${
            (err as Error).message
          }', reached max retries`,
        );
        this.logEvents = [];
      } else {
        console.warn(
          `Failed to send logs to Loggly with '${(err as Error).message}'`,
        );
      }
      this.retries += 1;
      this.startFlushTimeout(this.flushDelaySeconds * 2);
    }
  }
}
